'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '@pixum/icon';
import { Popup } from '@pixum/popup';
import { useAuthSessionStore } from 'stores/auth';
import { useUIStore } from 'stores/ui';
import type { UserPersonalInfo } from 'hooks';
import { trackEvent, TRACKING_ACTIONS, TRACKING_CATEGORIES } from 'utils';
import { getCurrentUrl, isLoggedInUser } from '@pixum/auth-core-ui';
import type { LinkSkeletonNode } from 'data-layer';
import { PopupContent } from '@pixum/web-header/lib/partials/PopupContent';
import { transformHeaderMenuItems } from '../../../../utils/transformHeaderMenuItems';
import styles from './AccountSectionContent.module.css';

type AccountSectionContentProps = {
  isAuthenticated: boolean;
  email?: string;
  menuItems: LinkSkeletonNode[];
  signInText: string;
  myPixumText: string;
};

/**
 * AccountSectionContent — client component for the streamed user section slot.
 *
 * Renders as a Fragment so the icon and text span are direct DOM children of
 * .web-header__item — required for the web-header's >span:first-of-type CSS
 * to control text visibility (desktop-only "Anmelden" / "Mein Pixum" label).
 *
 * The chevron indicator and popup open/closed state are communicated to the
 * parent web-header <li> by toggling web-header's own CSS classes directly on
 * it, so the existing ::after pseudo-element and colour rules apply natively
 * without duplicating any styles.
 */
export function AccountSectionContent({
  isAuthenticated,
  email,
  menuItems,
  signInText,
  myPixumText
}: AccountSectionContentProps) {
  const { setAuthUIVisibility } = useUIStore(state => state);
  const loginCount = useAuthSessionStore(state => state.loginCount);
  const [open, setOpen] = useState(false);
  const [offsetY, setOffsetY] = useState(35);
  const [isClientAuthenticated, setIsClientAuthenticated] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);
  const liRef = useRef<HTMLElement | null>(null);
  const currentUrl = getCurrentUrl();

  // Read the login state from the JWT cookie via isLoggedInUser, so it is a fact and not an assumption.
  useEffect(() => {
    setIsClientAuthenticated(isLoggedInUser());
  }, [loginCount, isAuthenticated]);

  const effectiveIsAuthenticated = isAuthenticated || isClientAuthenticated;

  const handleClick = useCallback(() => {
    if (!effectiveIsAuthenticated) {
      setAuthUIVisibility(true);
      trackEvent(TRACKING_CATEGORIES.HEADER, TRACKING_ACTIONS.LOGIN, currentUrl);
    } else {
      setOpen(prev => !prev);
    }
  }, [effectiveIsAuthenticated, setAuthUIVisibility, currentUrl]);

  // Routes clicks that land directly on the <li> (e.g. the chevron ::after pseudo-element) to
  // handleClick. Inner-element clicks are handled by their own React onClick handlers, so we
  // skip them here to avoid double-firing.
  const handleDirectLiClick = useCallback(
    (event: MouseEvent) => {
      if ((event.target as HTMLElement) === liRef.current) {
        handleClick();
      }
    },
    [handleClick]
  );

  // Cache the parent <li>, attach the arrow class (for the ::after chevron), and
  // keep offsetY up-to-date via ResizeObserver so the popup stays aligned after
  // any viewport or font-size reflow.
  useEffect(() => {
    const listElement = iconRef.current?.closest('li') as HTMLElement | null;
    liRef.current = listElement;

    if (!listElement) return;

    listElement.classList.add('web-header__item--is-clickable');

    if (!effectiveIsAuthenticated) {

      return () => {
        listElement.classList.remove('web-header__item--is-clickable');
      };
    }

    listElement.classList.add('web-header__item--with-arrow');
    setOffsetY(listElement.offsetHeight);

    const observer = new ResizeObserver(() => setOffsetY(listElement.offsetHeight));
    observer.observe(listElement);
    listElement.addEventListener('click', handleDirectLiClick);

    return () => {
      observer.disconnect();
      listElement.removeEventListener('click', handleDirectLiClick);
      listElement.classList.remove('web-header__item--is-clickable');
      listElement.classList.remove('web-header__item--with-arrow');
      listElement.classList.remove('web-header__popup--open');
    };
  }, [effectiveIsAuthenticated, handleDirectLiClick]);

  // Sync the open/closed chevron direction using the cached <li> ref.
  useEffect(() => {
    liRef.current?.classList.toggle('web-header__popup--open', open);
  }, [open]);

  const handleClose = useCallback(() => setOpen(false), []);

  const userInfo: UserPersonalInfo = { email, isAuthenticated: effectiveIsAuthenticated };
  const subItems = effectiveIsAuthenticated ? transformHeaderMenuItems(menuItems, userInfo) : [];

  const label = effectiveIsAuthenticated ? myPixumText : signInText;
  const iconName = effectiveIsAuthenticated ? 'user-loggedin' : 'user';
  // key forces a remount when iconName changes — Icon sets mask-image via a
  // useEffect with empty deps (mount-only), so changing the name prop alone
  // doesn't update the mask URL. Remounting ensures the effect re-runs.
  const icon = <Icon key={iconName} name={iconName} loading="eager" />;

  return (
    <>
      <div ref={iconRef} className={styles.icon} style={{ display: 'contents' }} onClick={handleClick}>
        {icon}
      </div>
      <span className="text--body" onClick={handleClick}>
        {label}
      </span>
      {open && subItems.length > 0 && (
        <Popup
          content={<PopupContent items={subItems} onClose={handleClose} />}
          onClose={handleClose}
          offsetY={offsetY}
        />
      )}
    </>
  );
}
